import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FirebaseContext } from 'common';
import { ref as databaseRef, onValue } from 'firebase/database';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Avatar,
  TextField,
  InputAdornment
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CircleIcon from '@mui/icons-material/Circle';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '../components/Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';

// Durées en ms
const PERIODS = {
  '24h': 24 * 60 * 60 * 1000,
  '7j':  7  * 24 * 60 * 60 * 1000,
  '1m':  30 * 24 * 60 * 60 * 1000,
};

const PERIOD_LABELS = {
  '24h': '24 heures',
  '7j':  '7 jours',
  '1m':  '30 jours',
};

/** Formate une durée en ms → "Xh Ymin" */
const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '0 min';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (h === 0) return `${min} min`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}min`;
};

/** Calcule le temps de connexion total d'un chauffeur sur une fenêtre donnée */
const computeOnlineTime = (sessions, since) => {
  let total = 0;
  const now = Date.now();
  for (const s of sessions) {
    const start = s.start || 0;
    const end   = s.end   || now; // session encore ouverte
    // Intersection avec la fenêtre [since, now]
    const clippedStart = Math.max(start, since);
    const clippedEnd   = Math.min(end, now);
    if (clippedEnd > clippedStart) {
      total += clippedEnd - clippedStart;
    }
  }
  return total;
};

export default function DriverTracker() {
  const firebase = useContext(FirebaseContext);

  const [drivers, setDrivers]   = useState([]);   // liste des chauffeurs
  const [logs, setLogs]         = useState({});    // { uid: [ {start, end}, ... ] }
  const [period, setPeriod]     = useState('24h');
  const [search, setSearch]     = useState('');
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingLogs, setLoadingLogs]       = useState(true);
  const [sortBy, setSortBy]   = useState('ms');
  const [sortDir, setSortDir] = useState('desc');

  // Charger les utilisateurs de type driver
  useEffect(() => {
    if (!firebase || !firebase.database) return;
    const usersRef = databaseRef(firebase.database, 'users');
    const unsub = onValue(usersRef, (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .filter(([, u]) => u.usertype === 'driver' && u.approved)
        .map(([uid, u]) => ({
          uid,
          name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || uid,
          email: u.email || '',
          mobile: u.mobile || '',
          active: Boolean(u.driverActiveStatus),
          source: u.source || '',
        }))
        .filter((d) => !d.source.includes('ghost'));
      setDrivers(list);
      setLoadingDrivers(false);
    });
    return () => unsub();
  }, [firebase]);

  // Charger les logs de connexion
  useEffect(() => {
    if (!firebase || !firebase.database) return;
    const logsRef = databaseRef(firebase.database, 'driverConnectionLogs');
    const unsub = onValue(
      logsRef,
      (snap) => {
        const val = snap.val() || {};
        // val = { uid: { sessionId: {start, end}, ... } }
        const parsed = {};
        for (const [uid, sessions] of Object.entries(val)) {
          parsed[uid] = Object.values(sessions);
        }
        setLogs(parsed);
        setLoadingLogs(false);
      },
      () => {
        // Permission refusée ou autre erreur : on affiche quand même le tableau sans logs
        setLoadingLogs(false);
      }
    );
    return () => unsub();
  }, [firebase]);

  const since = useMemo(() => Date.now() - PERIODS[period], [period]);

  const rows = useMemo(() => {
    return drivers.map((d) => {
      const sessions = logs[d.uid] || [];
      const ms = computeOnlineTime(sessions, since);
      const sessionCount = sessions.filter((s) => {
        const end = s.end || Date.now();
        return end >= since;
      }).length;
      const isOnline = d.active === true;
      return { ...d, ms, sessionCount, isOnline };
    });
  }, [drivers, logs, since]);

  const filtered = useMemo(() => {
    const base = search.trim()
      ? rows.filter((r) => {
          const q = search.toLowerCase();
          return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.mobile.includes(q);
        })
      : rows;
    return [...base].sort((a, b) => {
      let va, vb;
      if (sortBy === 'name')         { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); }
      else if (sortBy === 'isOnline'){ va = a.isOnline ? 1 : 0; vb = b.isOnline ? 1 : 0; }
      else if (sortBy === 'sessions'){ va = a.sessionCount;        vb = b.sessionCount; }
      else                           { va = a.ms;                  vb = b.ms; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, search, sortBy, sortDir]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  // Statistiques globales
  const stats = useMemo(() => {
    const online = rows.filter((r) => r.isOnline).length;
    const totalMs = rows.reduce((acc, r) => acc + r.ms, 0);
    const activeDrivers = rows.filter((r) => r.ms > 0).length;
    return { online, totalMs, activeDrivers };
  }, [rows]);

  const isLoading = loadingDrivers;

  const headerStyle = {
    backgroundColor: colors.Header,
    color: colors.WHITE,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  return (
    <div style={{ backgroundColor: '#f5f6fa', padding: '16px', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        {/* Titre */}
        <Box sx={headerStyle} borderRadius={2} mb={3}>
          <AccessTimeIcon sx={{ fontSize: 32 }} />
          <div>
            <Typography variant="h5" fontFamily={FONT_FAMILY} fontWeight={700}>
              Traqueur de connexion
            </Typography>
            <Typography variant="body2" fontFamily={FONT_FAMILY} sx={{ opacity: 0.8 }}>
              Temps de service actif des chauffeurs
            </Typography>
          </div>
        </Box>

        {/* Cartes stats */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontFamily={FONT_FAMILY} color="success.main" fontWeight={700}>
                  {stats.online}
                </Typography>
                <Typography variant="body2" fontFamily={FONT_FAMILY} color="text.secondary">
                  Chauffeurs connectés maintenant
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontFamily={FONT_FAMILY} color="primary.main" fontWeight={700}>
                  {stats.activeDrivers}
                </Typography>
                <Typography variant="body2" fontFamily={FONT_FAMILY} color="text.secondary">
                  Actifs sur la période sélectionnée
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontFamily={FONT_FAMILY} color="secondary.main" fontWeight={700}>
                  {formatDuration(stats.totalMs)}
                </Typography>
                <Typography variant="body2" fontFamily={FONT_FAMILY} color="text.secondary">
                  Temps cumulé tous chauffeurs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Sélecteur de période + recherche */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, v) => v && setPeriod(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: 'rgba(0,0,0,0.6)',
                borderColor: 'rgba(0,0,0,0.12)',
                fontFamily: FONT_FAMILY,
                textTransform: 'none',
                px: 3,
                '&.Mui-selected': {
                  backgroundColor: colors.Header,
                  color: '#fff',
                  '&:hover': { backgroundColor: colors.Header },
                },
              },
            }}
          >
            {Object.entries(PERIOD_LABELS).map(([key, label]) => (
              <ToggleButton key={key} value={key}>{label}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Rechercher un chauffeur…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              style: { fontFamily: FONT_FAMILY },
            }}
            sx={{ minWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
          />
        </Box>

        {/* Tableau */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {loadingLogs && (
              <Box sx={{ height: 3, background: `linear-gradient(90deg, ${colors.Header} 0%, rgba(255,255,255,0.2) 100%)`, animation: 'none', opacity: 0.7 }} />
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.Header }}>
                    {[
                      { label: 'Chauffeur',        col: 'name' },
                      { label: 'Statut',            col: 'isOnline' },
                      { label: 'Sessions',          col: 'sessions' },
                      { label: 'Temps connecté',    col: 'ms' },
                      { label: '% disponibilité',   col: 'ms' },
                    ].map(({ label, col }) => (
                      <TableCell
                        key={label}
                        sortDirection={sortBy === col ? sortDir : false}
                        sx={{ color: colors.WHITE, fontFamily: FONT_FAMILY, fontWeight: 700, cursor: 'pointer' }}
                      >
                        <TableSortLabel
                          active={sortBy === col}
                          direction={sortBy === col ? sortDir : 'desc'}
                          onClick={() => handleSort(col)}
                          sx={{
                            color: `${colors.WHITE} !important`,
                            '& .MuiTableSortLabel-icon': { color: `${colors.WHITE} !important` },
                          }}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, fontFamily: FONT_FAMILY, color: 'text.secondary' }}>
                        Aucun chauffeur trouvé
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((row) => {
                      const pct = Math.min(100, Math.round((row.ms / PERIODS[period]) * 100));
                      return (
                        <TableRow
                          key={row.uid}
                          hover
                          sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}
                        >
                          <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: colors.Header, fontSize: 14, fontFamily: FONT_FAMILY }}>
                                {row.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontFamily={FONT_FAMILY} fontWeight={600}>
                                  {row.name}
                                </Typography>
                                <Typography variant="caption" fontFamily={FONT_FAMILY} color="text.secondary">
                                  {row.mobile || row.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                row.isOnline
                                  ? 'Chauffeur connecté'
                                  : 'Chauffeur non connecté'
                              }
                            >
                              <Chip
                                size="small"
                                icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
                                label={
                                  row.isOnline
                                    ? 'Connecté'
                                    : 'Non connecté'
                                }
                                color={
                                  row.isOnline
                                    ? 'success'
                                    : 'default'
                                }
                                sx={{ fontFamily: FONT_FAMILY, fontSize: 11 }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                            <Tooltip title={`${row.sessionCount} session(s) sur la période`}>
                              <Typography variant="body2" fontFamily={FONT_FAMILY}>
                                {row.sessionCount}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontFamily={FONT_FAMILY}
                              fontWeight={row.ms > 0 ? 700 : 400}
                              color={row.ms > 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {formatDuration(row.ms)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  height: 6,
                                  width: 80,
                                  borderRadius: 3,
                                  backgroundColor: '#e0e0e0',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${pct}%`,
                                    backgroundColor: pct > 50 ? '#4caf50' : pct > 20 ? '#ff9800' : '#f44336',
                                    borderRadius: 3,
                                    transition: 'width 0.4s ease',
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontFamily={FONT_FAMILY} color="text.secondary">
                                {pct}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        <Typography variant="caption" color="text.secondary" fontFamily={FONT_FAMILY} sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
          Les temps sont calculés à partir du premier toggle chauffeur actif/inactif après déploiement de la fonction de suivi.
        </Typography>
      </Box>
    </div>
  );
}
